import React from "react"

import { Box } from "@mui/material"

import { t } from "@lingui/macro"

import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"

import withUseRouter, { UseRouterParams } from "services/router"
import { BrandManagementServices } from "services/brand-management.services"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import { BrandAdvancedSearch } from "components/brand/advanced-search"
import {
	PageHeader,
	PageSectionTitle,
	RoutesData,
} from "components/commons"
import { BrandTable } from "components/brand/brand-table/brandTable"
import { CreateBrandModal } from "components/brand"
import {
	BrandManagementRequest,
	Brand,
	CreateBrandRequest,
	CreateBrandResponse,
	Company,
	PaginatedResponse,
	Product
} from "models"


interface BrandManagementProps extends UseRouterParams
{
}
interface BrandManagementState extends MatesComponentState
{
	brandsData: PaginatedResponse<Brand> | null
	request: BrandManagementRequest | null
	openModalCreate: boolean
	companyList: PaginatedResponse<Company> | null,
	products: PaginatedResponse<Product> | null
}
class BrandManagement extends React.Component<BrandManagementProps, BrandManagementState>
{
    private readonly matesComponent: MatesComponent<BrandManagementProps, BrandManagementState>
		= new MatesComponent(this)

	constructor(props: BrandManagementProps)
	{
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			brandsData: null,
			request: {
				orderBy: "name",
				orderType: "ASCENDING",
				pageId: 0,
				pageSize: 10
			},
			loading: true,
			openModalCreate: false,
			companyList: null,
			products: null,
		}
	}

	componentDidMount()
	{
		this.filterBrands()
		this.retrieveProducts()
	}

	private filterBrands = (): void => {
		if (this.state.request){
			BrandManagementServices.brandManagementData(
				this.state.request,
				this.updateBrandManagementData,
				this.matesComponent.onException,
			)
		}
	}

	private updateBrandManagementData = (response: RestResponse<PaginatedResponse<Brand>, ErrorResponse>): void => {
		if(response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

        this.setState(() => ({
            brandsData: response.data,
            loading: false
        }))
    }

	private retrieveProducts = () => {
		BrandManagementServices.getProducts(this.updateProducts, this.matesComponent.onException)
	}

	private updateProducts = (response: RestResponse<PaginatedResponse<Product>, ErrorResponse>): void => {
		if(response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			products: response.data,
		}))
	}

	private handleSearchbarCallback = (data: any) => {
		this.setState(() => ({
			loading: true,
			request: {
				name: data.name,
				companyName: data.companyName,
				products: data.products,
				minActiveContracts: data.minActiveContracts,
				maxActiveContracts: data.maxActiveContracts,
				pageSize: this.state.request?.pageSize,
				pageId: 0,
			},
		}), this.filterBrands)
	}

	private handleTableCallback = (data: any) => {
		this.setState(() => ({
			loading: true,
			request: {
				name: this.state.request?.name,
				companyName: this.state.request?.companyName,
				products: this.state.request?.products,
				minActiveContracts: this.state.request?.minActiveContracts,
				maxActiveContracts: this.state.request?.maxActiveContracts,
				pageSize: data.pageSize,
				pageId: data.pageId,
				orderBy: data.orderBy,
				orderType: data.orderType
			},
		}), this.filterBrands)
	}

	private getCompanyCallback = (response: RestResponse<PaginatedResponse<Company>, ErrorResponse>): void => {
		if(response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			loading: false,
			companyList: response.data,
			openModalCreate: true,
		}))
	}

	private openModalCreate = (): void => {
		BrandManagementServices.getCompany(this.getCompanyCallback, this.matesComponent.onError)
	}

	private closeModalCreate = (): void => {
		this.setState(() => ({openModalCreate: false}))
	}

	private createBrand = (values: any) => {
		if (values.createNewCompany && values.companyData) {
			// Prima crea la società, poi il brand
			this.createCompanyAndBrand(values)
		} else {
			// Crea solo il brand con società esistente
			let request: CreateBrandRequest = {
				name: values.name,
				company: values.company,
			}
			this.closeModalCreate()
			this.setState(() => ({loading: true}))
			BrandManagementServices.createBrand(request, this.createBrandCallback, this.matesComponent.onError)
		}
	}

	private createCompanyAndBrand = (values: any) => {
		this.closeModalCreate()
		this.setState(() => ({loading: true}))
		
		// Prima crea la società
		BrandManagementServices.createCompany(
			values.companyData,
			(companyResponse: any) => {
				if (companyResponse.hasError()) {
					this.matesComponent.onError(companyResponse)
					return
				}
				
				// Poi crea il brand con l'ID della società appena creata
				let brandRequest: CreateBrandRequest = {
					name: values.name,
					company: companyResponse.data.id,
				}
				
				BrandManagementServices.createBrand(
					brandRequest,
					this.createBrandCallback,
					this.matesComponent.onError
				)
			},
			this.matesComponent.onError
		)
	}

	private createBrandCallback = (response: RestResponse<CreateBrandResponse, ErrorResponse>): void => {
		if(response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		this.setState(() => ({
			loading: false,
		}))

		const {navigate} = this.props
		navigate(`/brand/${response.data?.id}`)
	}

	render()
	{
		return (
			<Box sx={{width: "100%", pt: 2, position: "relative" }}>
				<Loading show={this.state.loading} />
				<PageHeader
					title={RoutesData.BRAND_LIST.title}
					paths={[ RoutesData.DASHBOARD, RoutesData.BRAND_LIST ]}
					addButtonModal={"Aggiungi Brand"}
					onClickButtonModal={this.openModalCreate}
				/>

				{this.state.products !== null &&
					<BrandAdvancedSearch
						handleCallback={this.handleSearchbarCallback}
						products={this.state.products} />
				}

				<Box sx={{mt: 3}}>
					<PageSectionTitle title={t({ id: "brand-management.brand", message: "Brand" })} />
					{
						this.state.brandsData &&
						<BrandTable data={this.state.brandsData} handleCallback={this.handleTableCallback} />
					}
				</Box>

				{this.state.openModalCreate &&
					<CreateBrandModal
						closeModal={this.closeModalCreate}
						submitForm={this.createBrand}
						companyList={this.state.companyList!} />
				}
			</Box>
		)
	}
}

export default withUseRouter(BrandManagement)
